import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthenticatedUser } from '@omnea-app/fe-auth/UserSessionProvider'
import { throwIfApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { UsersHttpClient } from '@omnea-app/users/usersHttpClient'

const getUsersByIdQueryKey = (userId: string) => ['getUserById_normalised', userId]
const usersByIdStaleTime = 5 * 60 * 1000 // 5 minutes

const isStale = (dataUpdatedAt: number | undefined) =>
  dataUpdatedAt != null ? Date.now() - dataUpdatedAt > usersByIdStaleTime : true

type User = {
  /** Format: uuid */
  id: string
  firstName: string
  lastName: string
  email: string
  isArchived: boolean
  /** @enum {string} */
  role: 'user' | 'editor'
  isOutOfOffice?: boolean
}

export const useSetUsersByIdCache = () => {
  const queryClient = useQueryClient()

  return (users: User[]) => {
    users.forEach((user) => {
      queryClient.setQueryData<User>(getUsersByIdQueryKey(user.id), user)
    })
  }
}

export const usersByIdQueryKey = 'getUsersByIds'

export const useGetUsersByIds = ({ userIds }: { userIds: string[] }) => {
  const { getToken } = useAuthenticatedUser()

  const queryClient = useQueryClient()
  const setUsersByIdCache = useSetUsersByIdCache()

  return useQuery({
    queryKey: [usersByIdQueryKey, userIds.slice().sort()], // Sort for consistent cache key
    queryFn: async () => {
      const userIdsToFetch = []
      const usersById: Record<string, User> = {}

      for (const userId of userIds) {
        const queryState = queryClient.getQueryState<User>(getUsersByIdQueryKey(userId))
        // We still populate our final result with fresh data from the cache.
        if (queryState?.data && !isStale(queryState.dataUpdatedAt)) {
          usersById[userId] = queryState.data
        } else {
          // If data is missing or stale, we must fetch it.
          userIdsToFetch.push(userId)
          // We can also add the stale data to our result map for now.
          if (queryState?.data) {
            usersById[userId] = queryState.data
          }
        }
      }

      // If we need to do some fetching, let's do it
      if (userIdsToFetch.length > 0) {
        const token = await getToken()
        const apiClient = new UsersHttpClient({ omneaJwt: token })
        const { data, error } = await apiClient.POST('/by-ids', {
          body: {
            userIds: userIdsToFetch,
          },
        })
        throwIfApiError(error)

        setUsersByIdCache(data)
        data.forEach((user) => {
          usersById[user.id] = user
        })
      }

      // Merge results from cache and fetched data
      return userIds.map((id) => usersById[id]).filter((user) => user != null)
    },
    // If we have all of the users in the cache already,
    // we can return users before firing the queryFn (which may need us to refetch the stale ones)
    placeholderData: (previousData) => {
      const users = []
      for (const userId of userIds) {
        const user = queryClient.getQueryData<User>(getUsersByIdQueryKey(userId))
        if (user) {
          users.push(user)
        }
      }
      if (users.length === userIds.length) return users
      // While the new set loads, keep previously loaded users that are still requested (so a growing list
      // doesn't flicker to empty), but never users that dropped out of the set. With nothing to carry over,
      // return undefined so the query shows a genuine loading state rather than an empty success.
      const carriedOver = previousData?.filter((user) => userIds.includes(user.id)) ?? []
      return carriedOver.length > 0 ? carriedOver : undefined
    },
    enabled: userIds.length > 0,
    gcTime: 1000 * 60 * 5, // 5 minutes - we'll keep the normalised data around for longer anyway (30 mins - default GC time)
  })
}

// This hook is useful for when you only need a single user by ID
// Shares the same normalised cache with useGetUsersByIds
export const useGetSingleUserById = (userId: string | null | undefined) => {
  const { getToken } = useAuthenticatedUser()
  return useQuery({
    queryKey: getUsersByIdQueryKey(userId as string),
    queryFn: async () => {
      if (userId == null) {
        // Should never happen - enabled will be false
        throw new Error('userId is required')
      }
      const token = await getToken()
      const apiClient = new UsersHttpClient({ omneaJwt: token })
      const { data, error } = await apiClient.POST('/by-ids', {
        body: {
          userIds: [userId],
        },
      })

      throwIfApiError(error)
      return data?.[0] ?? null
    },
    staleTime: usersByIdStaleTime,
    enabled: userId != null,
  })
}
